import { useTranslation } from "react-i18next";
import { cn } from "@erp/ui";

import {
  formatExactMoney,
  formatMoney,
  type FormatMoneyOptions,
  type MoneyValue,
} from "@/lib/money/money";

export interface MoneyDisplayProps {
  value: MoneyValue;
  /** "symbol" (default) | "code" | "none" — passed through to formatMoney. */
  display?: FormatMoneyOptions["display"];
  className?: string;
}

/**
 * Read-only money renderer. Formats through the money core (never a JS
 * number) in the active i18n locale, with tabular figures so columns of
 * amounts align.
 */
export function MoneyDisplay({ value, display = "symbol", className }: MoneyDisplayProps) {
  const { i18n } = useTranslation();
  const locale = i18n.language;

  let formatted: string;
  try {
    formatted = formatMoney(value, locale, { display });
  } catch {
    try {
      // An amount carrying more decimals than the currency's smallest coin is
      // exact, not malformed — a ledger total is stored at four. Show every
      // digit rather than rounding money for the sake of a tidy column.
      formatted = formatExactMoney(value, locale, { display });
    } catch {
      // A genuinely malformed wire amount must not crash the screen.
      formatted = display === "none" ? value.amount : `${value.amount} ${value.currency}`;
    }
  }

  return (
    <span
      // physical-ok: numerals render LTR even in RTL text
      dir="ltr"
      className={cn("tnum tabular-nums", className)}
    >
      {formatted}
    </span>
  );
}
